import { memo, useCallback, useState } from "react";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { clientService } from "@/features/clients/services/client.service";
import type { ClientResponseDTO } from "@/features/clients/types/client.types";
import { Input } from "@/shared/components/ui/input";
import { Spinner } from "@/shared/components/ui/spinner";
import { useDebouncedSearch } from "@/shared/hooks/useDebouncedSearch";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import { formatCPF, formatPhone } from "@/shared/lib/formatters";

interface InvoiceClientSearchProps {
  onSelect: (client: ClientResponseDTO) => void;
}

const SEARCH_DEBOUNCE_MS = 300;

export const InvoiceClientSearch = memo(function InvoiceClientSearch({
  onSelect,
}: InvoiceClientSearchProps) {
  const [term, setTerm] = useState("");
  const [results, setResults] = useState<ClientResponseDTO[]>([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);

  const fetchClients = useCallback(
    async (rawTerm: string, signal: AbortSignal) => {
      const trimmed = rawTerm.trim();
      const isDigits = /^\d+$/.test(trimmed);
      const response = await clientService.search(
        isDigits
          ? { document: trimmed, size: 10 }
          : { name: trimmed, size: 10 },
        { signal },
      );
      setShowResults(true);
      return response.content;
    },
    [],
  );

  useDebouncedSearch({
    term,
    fetcher: fetchClients,
    setItems: setResults,
    setLoading: setSearching,
    isValid: (t) => t.trim().length >= 2,
    onError: (e) =>
      toast.error(getApiErrorMessage(e, "Erro ao buscar clientes")),
    debounceMs: SEARCH_DEBOUNCE_MS,
  });

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Busque por nome ou CPF/CNPJ"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          className="pl-8"
        />
        {searching ? (
          <Spinner className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4" />
        ) : null}
      </div>

      {showResults && results.length > 0 ? (
        <div className="rounded-md border border-input bg-background max-h-64 overflow-y-auto">
          {results.map((client) => (
            <button
              type="button"
              key={client.id}
              onClick={() => {
                onSelect(client);
                setTerm("");
                setShowResults(false);
              }}
              className="w-full text-left px-3 py-2 hover:bg-accent border-b border-border last:border-b-0"
            >
              <div className="text-sm font-medium">{client.name}</div>
              <div className="text-xs text-muted-foreground">
                {formatCPF(client.document)} · {formatPhone(client.phone)}
              </div>
            </button>
          ))}
        </div>
      ) : null}

      {showResults && !searching && results.length === 0 && term.length >= 2 ? (
        <p className="text-xs text-muted-foreground">
          Nenhum cliente encontrado. Use "Cadastrar novo" para informar os
          dados.
        </p>
      ) : null}
    </div>
  );
});
